import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { queryKeys, unwrap, type components } from '@geefamily/api';
import { api } from '@/app/api';
import { useAuth } from '@/features/auth/use-auth';

export type TicketEvent = components['schemas']['TicketEventResponse'];
export type TicketEventDetail = components['schemas']['TicketEventDetailResponse'];
export type TicketParticipation = components['schemas']['TicketParticipationResponse'];

const keys = queryKeys.module('tickets').entity('events');

/**
 * Short, because an event moves between running and ended on the clock alone.
 * The server computes the phase after its cache, so this can lag by a poll but
 * never by a cache window.
 */
const StaleTime = 30_000;

export function useTicketEvents() {
  return useQuery({
    queryKey: keys.lists(),
    queryFn: () => unwrap<TicketEvent[]>(api.GET('/api/v1/tickets', {})),
    staleTime: StaleTime,
  });
}

/**
 * The caller's saved identifier is part of this response, so the key carries the
 * member and the fetch waits for the session probe. Without that, a reload
 * fetched anonymously before the probe settled and the cached answer then said
 * "no identifier" to a member who had one.
 */
export function useTicketEvent(slug: string | undefined) {
  const { user, isLoading } = useAuth();

  return useQuery({
    queryKey: [...keys.detail(slug ?? ''), user?.id ?? 'guest'],
    queryFn: () =>
      unwrap<TicketEventDetail>(
        api.GET('/api/v1/tickets/{slug}', { params: { path: { slug: slug! } } }),
      ),
    enabled: Boolean(slug) && !isLoading,
    staleTime: StaleTime,
  });
}

/**
 * Submits the deposit, then refreshes the detail so the row appears in place.
 * Retry is off: the request is a write, and the server already resolves a
 * double tap into one row.
 */
export function useJoinTicketEvent(slug: string) {
  const client = useQueryClient();

  return useMutation({
    mutationFn: (body: { requestId: string; depositAmount: number; depositedAtUtc: string }) =>
      unwrap<TicketParticipation>(
        api.POST('/api/v1/tickets/{slug}/participations', {
          params: { path: { slug } },
          body,
        }),
      ),
    retry: false,
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.detail(slug) });
      void client.invalidateQueries({ queryKey: keys.lists() });
    },
  });
}
